import { useCallback } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { FormController } from '@/components/ui/FormController'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { TextField } from '@/components/ui/text-field'
import {
  RecentSpendListFormSchema,
  type RecentSpendListFormValues,
} from '@/modules/creator-settings/lists/recent-spenders/types'

import type { DialogProps } from '../types'

interface RecentSpenderFormDialogProps extends DialogProps {
  title: string
  initialValues?: RecentSpendListFormValues
  submitLabel?: string
  isLoading?: boolean
  onSubmit: (values: RecentSpendListFormValues) => Promise<unknown | undefined>
}

export const RecentSpenderFormDialog: React.FC<
  RecentSpenderFormDialogProps
> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  initialValues,
  submitLabel = 'Save',
  isLoading = false,
}) => {
  const form = useForm<RecentSpendListFormValues>({
    resolver: zodResolver(RecentSpendListFormSchema),
    defaultValues: {
      name: initialValues?.name ?? '',
      minimumMinutes: initialValues?.minimumMinutes ?? 1,
    },
    mode: 'onChange',
  })

  const { handleSubmit } = form

  const handleFormSubmit = useCallback(
    async (values: RecentSpendListFormValues) => {
      await onSubmit(values)

      onClose()
    },
    [onClose, onSubmit],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      panelClassName="w-[400px]"
      testId="recent-spender-list-modal"
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Dialog.Header>
            <Dialog.Title>{title}</Dialog.Title>
          </Dialog.Header>
          <Dialog.Description>
            <div className="flex flex-col gap-6 pt-6">
              <Controller
                name="name"
                render={({ field, fieldState }) => (
                  <FormController>
                    <FormController.Label htmlFor="list-name">
                      List Name
                    </FormController.Label>
                    <FormController.Input>
                      <TextField
                        {...field}
                        onBlur={() => {
                          field.onBlur()
                          field.onChange(field.value.trim())
                        }}
                        onChange={field.onChange}
                        id="list-name"
                        placeholder="Enter list name"
                        testId="list-name"
                      />
                    </FormController.Input>
                    {fieldState.error?.message && (
                      <FormController.Error>
                        {fieldState.error?.message}
                      </FormController.Error>
                    )}
                  </FormController>
                )}
              />
              <Controller
                name="minimumMinutes"
                render={({ field, fieldState }) => (
                  <FormController>
                    <FormController.Label htmlFor="minimum-minutes">
                      Minimum
                    </FormController.Label>
                    <FormController.Input>
                      <TextField
                        {...field}
                        onChange={field.onChange}
                        id="minimum-minutes"
                        type="number"
                        placeholder="Enter minimum minutes"
                        suffix="Minutes"
                        showSuffixSeparator
                        testId="minimum-minutes"
                      />
                    </FormController.Input>
                    {!fieldState.error?.message && (
                      <FormController.Helper>
                        Minimum 1 minute for custom recent spend list
                      </FormController.Helper>
                    )}
                    {fieldState.error?.message && (
                      <FormController.Error>
                        {fieldState.error?.message}
                      </FormController.Error>
                    )}
                  </FormController>
                )}
              />
            </div>
          </Dialog.Description>
          <Dialog.Footer>
            <Button
              type="button"
              onClick={onClose}
              className="flex-1"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              isLoading={isLoading || form.formState.isSubmitting}
              className="flex-1"
              testId="recent-spender-list-submit-button"
            >
              {submitLabel}
            </Button>
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
